(() => {
  'use strict';

  if (window.__spiritCarouselBoundariesLoaded) return;
  window.__spiritCarouselBoundariesLoaded = true;

  const tolerance = 3;
  const trackedRails = new WeakMap();
  const controlBindings = new WeakMap();

  const visibleChildren = (rail) =>
    Array.from(rail.children).filter((item) => {
      const rect = item.getBoundingClientRect();
      return !item.hidden && rect.width > 0 && rect.height > 0;
    });

  const getViewport = (rail) => {
    const rect = rail.getBoundingClientRect();
    const styles = getComputedStyle(rail);
    const leftInset = Number.parseFloat(styles.paddingLeft) || 0;
    const rightInset = Number.parseFloat(styles.paddingRight) || 0;

    return {
      left: rect.left + leftInset,
      right: rect.right - rightInset,
    };
  };

  const getRailState = (rail) => {
    const items = visibleChildren(rail);
    const hasOverflow = rail.scrollWidth > rail.clientWidth + tolerance;

    if (!hasOverflow || !items.length) {
      return { hasOverflow: false, canScrollLeft: false, canScrollRight: false };
    }

    const viewport = getViewport(rail);

    return {
      hasOverflow: true,
      canScrollLeft: items.some((item) => item.getBoundingClientRect().left < viewport.left - tolerance),
      canScrollRight: items.some((item) => item.getBoundingClientRect().right > viewport.right + tolerance),
    };
  };

  const setControlState = (button, { disabled, hidden }) => {
    if (!button) return;

    button.dataset.spiritCarouselBoundaryControl = 'true';
    button.hidden = hidden;
    button.disabled = disabled;
    button.setAttribute('aria-disabled', String(disabled));
    button.classList.toggle('is-spirit-carousel-boundary', disabled);
  };

  const syncRail = (rail, previousButton, nextButton) => {
    const { hasOverflow, canScrollLeft, canScrollRight } = getRailState(rail);

    // Previous always moves visibly left and next always moves visibly right.
    // This visual mapping stays correct for both RTL and LTR rails.
    setControlState(previousButton, {
      disabled: !hasOverflow || !canScrollLeft,
      hidden: !hasOverflow,
    });
    setControlState(nextButton, {
      disabled: !hasOverflow || !canScrollRight,
      hidden: !hasOverflow,
    });
  };

  const getTargetInDirection = (rail, direction) => {
    const viewport = getViewport(rail);
    const items = visibleChildren(rail).map((item) => ({ item, rect: item.getBoundingClientRect() }));

    if (direction < 0) {
      return items
        .filter(({ rect }) => rect.left < viewport.left - tolerance)
        .sort((first, second) => second.rect.right - first.rect.right)[0]?.item;
    }

    return items
      .filter(({ rect }) => rect.right > viewport.right + tolerance)
      .sort((first, second) => first.rect.left - second.rect.left)[0]?.item;
  };

  const scrollRail = (rail, direction) => {
    const target = getTargetInDirection(rail, direction);
    if (!target) return;

    const viewport = getViewport(rail);
    const targetBounds = target.getBoundingClientRect();

    // Move only the rail; scrollIntoView can also scroll the page and nested ancestors.
    rail.scrollBy({
      left: direction < 0 ? targetBounds.left - viewport.left : targetBounds.right - viewport.right,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
    });
  };

  const registerRail = (rail, previousButton, nextButton) => {
    if (!rail || !previousButton || !nextButton) return;

    controlBindings.set(previousButton, { rail, direction: -1, previousButton, nextButton });
    controlBindings.set(nextButton, { rail, direction: 1, previousButton, nextButton });

    if (trackedRails.has(rail)) {
      syncRail(rail, previousButton, nextButton);
      return;
    }

    let frame = 0;
    const sync = () => {
      frame = 0;
      if (!rail.isConnected) return;
      syncRail(rail, previousButton, nextButton);
    };
    const requestSync = () => {
      if (frame) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(sync);
    };

    rail.addEventListener('scroll', requestSync, { passive: true });

    const resizeObserver = new ResizeObserver(requestSync);
    resizeObserver.observe(rail);

    const mutationObserver = new MutationObserver(requestSync);
    mutationObserver.observe(rail, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'hidden', 'style'],
    });

    trackedRails.set(rail, { resizeObserver, mutationObserver });
    requestSync();
  };

  const byId = (id) => (id ? document.getElementById(id) : null);

  const setupResultRails = (scope) => {
    scope.querySelectorAll('[data-spirit-rail-controls]').forEach((controls) => {
      registerRail(
        byId(controls.dataset.spiritRailControls),
        controls.querySelector('[data-spirit-rail-previous]'),
        controls.querySelector('[data-spirit-rail-next]')
      );
    });
  };

  const setupTreatmentRails = (scope) => {
    scope.querySelectorAll('[data-spirit-treatments-rail]').forEach((rail) => {
      const section = rail.closest('.spirit-treatments');
      registerRail(
        rail,
        section?.querySelector('[data-spirit-treatments-previous]'),
        section?.querySelector('[data-spirit-treatments-next]')
      );
    });
  };

  const setupCommunityRails = (scope) => {
    scope.querySelectorAll('[data-spirit-community-rail]').forEach((rail) => {
      const target = rail.id;
      const section = rail.closest('.shopify-section') || document;
      registerRail(
        rail,
        section.querySelector(
          `[data-spirit-community-rail-previous][data-spirit-community-rail-target="${CSS.escape(target)}"]`
        ),
        section.querySelector(
          `[data-spirit-community-rail-next][data-spirit-community-rail-target="${CSS.escape(target)}"]`
        )
      );
    });
  };

  const setupGalleryRails = (scope) => {
    scope.querySelectorAll('[data-gallery-track]').forEach((rail) => {
      const gallery = rail.closest('spirit-product-gallery');
      registerRail(
        rail,
        gallery?.querySelector('[data-gallery-scroll-prev]'),
        gallery?.querySelector('[data-gallery-scroll-next]')
      );
    });
  };

  const setupProviderRails = (scope) => {
    scope.querySelectorAll('[data-provider-rail-track]').forEach((rail) => {
      const providerRail = rail.closest('spirit-provider-rail');
      registerRail(
        rail,
        providerRail?.querySelector('[data-provider-rail-previous]'),
        providerRail?.querySelector('[data-provider-rail-next]')
      );
    });
  };

  const setupHostRails = (scope) => {
    scope.querySelectorAll('[data-host-track]').forEach((rail) => {
      const hosts = rail.closest('.spirit-event-hosts');
      registerRail(
        rail,
        hosts?.querySelector('[data-host-scroll-prev]'),
        hosts?.querySelector('[data-host-scroll-next]')
      );
    });
  };

  const setupDirectoryRails = (scope) => {
    scope.querySelectorAll('[data-directory-provider-rail]').forEach((rail) => {
      const directory = rail.closest('[data-spirit-directory]');
      registerRail(
        rail,
        directory?.querySelector('[data-directory-rail-button="previous"]'),
        directory?.querySelector('[data-directory-rail-button="next"]')
      );
    });
  };

  const setupRelatedRails = (scope) => {
    scope.querySelectorAll('[data-related-track]').forEach((rail) => {
      const related = rail.closest('[data-related-events]');
      registerRail(
        rail,
        related?.querySelector('[data-related-scroll="-1"]'),
        related?.querySelector('[data-related-scroll="1"]')
      );
    });
  };

  const setup = (scope = document) => {
    setupResultRails(scope);
    setupTreatmentRails(scope);
    setupCommunityRails(scope);
    setupGalleryRails(scope);
    setupProviderRails(scope);
    setupHostRails(scope);
    setupDirectoryRails(scope);
    setupRelatedRails(scope);
  };

  document.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest('button');
      const binding = button ? controlBindings.get(button) : null;
      if (!binding || button.disabled || button.hidden) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      scrollRail(binding.rail, binding.direction);
      requestAnimationFrame(() => syncRail(binding.rail, binding.previousButton, binding.nextButton));
    },
    true
  );

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => setup());
  } else {
    setup();
  }

  document.addEventListener('shopify:section:load', (event) => setup(event.target));
})();
